import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  ImageStyle,
  Platform,
  StyleSheet,
  TextStyle,
  useWindowDimensions,
  ViewStyle,
  type ScaledSize,
} from 'react-native';

import { computeScaling, type ScaleSize } from '@/theme/scaling';

/**
 * Same source as `useWindowDimensions`, but on web coalesces resize bursts to one
 * update per animation frame so `useScaling()` does not re-render the whole tree
 * on every intermediate width/height event.
 */
function useResponsiveWindowSize(): Pick<ScaledSize, 'width' | 'height'> {
  const [size, setSize] = useState<ScaledSize>(() => Dimensions.get('window'));
  const rafId = useRef<number | null>(null);
  const pending = useRef<ScaledSize | null>(null);

  useEffect(() => {
    const applyWeb = () => {
      rafId.current = null;
      const next = pending.current;
      pending.current = null;
      if (!next) return;
      setSize(prev => (prev.width === next.width && prev.height === next.height ? prev : next));
    };

    const sub = Dimensions.addEventListener('change', ({ window }) => {
      if (Platform.OS === 'web') {
        pending.current = window;
        if (rafId.current != null) return;
        rafId.current = requestAnimationFrame(applyWeb);
      }
    });

    return () => {
      sub.remove();
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, []);

  return { width: size.width, height: size.height };
}

const useWindowDimensionsPlatform =
  Platform.OS === 'web' ? useResponsiveWindowSize : useWindowDimensions;

/**
 * Reactive scaling hook — recomputes on dimension changes (rotation, split-view).
 * Drop-in replacement for the static `scale` / `s` / `scaleVertical` functions.
 */
export function useScaling() {
  const { width, height } = useWindowDimensionsPlatform();

  const metrics = useMemo(() => computeScaling(width, height), [width, height]);

  const scale = useCallback(
    (size: ScaleSize): number => {
      if (typeof size === 'number') {
        return size * metrics.scaleFactor;
      }
      return metrics.isTablet
        ? size.tablet * metrics.scaleFactor
        : size.phone * metrics.scaleFactor;
    },
    [metrics.scaleFactor, metrics.isTablet],
  );
  const scaleVertical = useCallback(
    (size: number): number => size * metrics.scaleHeight,
    [metrics.scaleHeight],
  );

  return useMemo(
    () => ({
      scale,
      scaleVertical,
      ...metrics,
    }),
    [scale, scaleVertical, metrics],
  );
}

export type Scaling = ReturnType<typeof useScaling>;

type RNStyle = ViewStyle | TextStyle | ImageStyle;
type NamedStyles = Record<string, RNStyle>;

/**
 * Factory that returns a `useStyles` hook with reactive scaling.
 *
 * @example
 * const useStyles = createStyles(({ scale }) => ({
 *   label: { marginTop: scale(2), fontSize: scale(8) },
 * }));
 *
 * function MyComponent() {
 *   const styles = useStyles();
 *   return <Text style={styles.label} />;
 * }
 */
export function createStyles<T extends NamedStyles>(factory: (scaling: Scaling) => T) {
  return (): T => {
    const scaling = useScaling();
    return useMemo(() => StyleSheet.create(factory(scaling)), [scaling]);
  };
}
